import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import CrudService from "services/cruds-service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import Footer from "../../../../../../examples/Footer";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Slide from "@mui/material/Slide";
import SearchIcon from "@mui/icons-material/Search";
import Card from "@mui/material/Card";
import Avatar from "@mui/material/Avatar";
import OutlinedInput from "@mui/material/OutlinedInput";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { VectorMap } from "@react-jvectormap/core";
import { worldMerc } from "@react-jvectormap/world";
import countryOptions from "../../../new-product/components/List/Countries";
import DataTable from "../../../../../../examples/Tables/DataTable";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Select, TextField } from "@mui/material";
import { jwtDecode } from "jwt-decode";
import PublicIcon from "@mui/icons-material/Public";
import RfqFormDialog from "./RfqFormDialog";
import { appConfirm } from "components/AppDialog";

import { CrmRowActions, CrmPageHeader, CrmActionButton, CrmTableCard, ListLink, ListText, ListTruncate } from "../shared/CrmUi";
import Add from "@mui/icons-material/Add";
dayjs.extend(utc);
dayjs.extend(timezone);

const COUNTRY_ALIASES = {
    "U.S.A.": "United States",
    USA: "United States",
    US: "United States",
    "United States of America": "United States",
    UK: "United Kingdom",
    Türkiye: "Türkiye",
    Turkey: "Türkiye",
    "Republic of Turkey": "Türkiye",
};

const STATUS_META = {
    pending: { label: "Pending", color: "#FFB300" },
    sent: { label: "Sent", color: "#1E88E5" },
   /* progress: { label: "In Progress", color: "#8E24AA" },
    secured: { label: "Secured", color: "#43A047" },*/
    cancelled: { label: "Cancelled", color: "#E53935" },
    closed: { label: "Closed", color: "#546E7A" },
};

// Artık seçilemeyen ama eski kayıtlarda bulunan durumlar. Eskiden bunlar "Pending" olarak
// görünüyordu (kazanılmış/kaybedilmiş bir RFQ beklemede gibi duruyordu); şimdi doğru adıyla gösterilir.
const LEGACY_STATUS_META = {
    progress: { label: "In Progress", color: "#8E24AA" },
    secured: { label: "Secured", color: "#43A047" },
    lost: { label: "Lost", color: "#6D4C41" },
};
const getStatusMeta = (key) =>
    STATUS_META[key] ||
    LEGACY_STATUS_META[key] || {
        label: key ? String(key).charAt(0).toUpperCase() + String(key).slice(1) : STATUS_META.pending.label,
        color: "#9E9E9E",
    };

const StatusDot = ({ status }) => {
    const key = String(status || "pending").toLowerCase();
    const meta = getStatusMeta(key);
    return (
        <span
            style={{
                display: "inline-block",
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: meta.color,
                marginRight: 8,
                verticalAlign: "middle",
            }}
        />
    );
};

const canonicalCountry = (name) => {
    if (!name) return "";
    const t = String(name).trim();
    return COUNTRY_ALIASES[t] || t;
};

function extractCountryNames(raw) {
    if (!raw) return [];
    const out = [];
    const pushIf = (v) => {
        if (v !== undefined && v !== null && String(v).trim() !== "") out.push(String(v));
    };
    if (Array.isArray(raw)) raw.forEach((x) => out.push(...extractCountryNames(x)));
    else if (typeof raw === "object")
        ["globalAgentCountry", "country", "label", "name", "value", "title"].forEach((k) => pushIf(raw[k]));
    else pushIf(raw);

    return Array.from(
        new Set(
            out
                .map((s) => {
                    let t = String(s).trim();
                    if (t.includes(",")) {
                        const last = t
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean)
                            .pop();
                        if (last) t = last;
                    }
                    return t;
                })
                .filter(Boolean)
        )
    );
}

const COUNTRY_COORDS = {
    Germany: [51.1657, 10.4515],
    Turkey: [39.9334, 32.8597],
    Türkiye: [39.9334, 32.8597],
    "United Kingdom": [51.5074, -0.1278],
    "United States": [38.8951, -77.0364],
    France: [48.8566, 2.3522],
    Italy: [41.9028, 12.4964],
    Spain: [40.4168, -3.7038],
    Netherlands: [52.1326, 5.2913],
    Belgium: [50.8503, 4.3517],
    China: [39.9042, 116.4074],
    Japan: [35.6762, 139.6503],
    India: [28.6139, 77.209],
};

const coordsCache = new Map();
async function fetchCountryCoordinates(countryName, signal) {
    const q = encodeURIComponent(countryName);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/locations/country-centroid?country=${q}`, {
        signal,
        headers: { Accept: "application/json" },
    });
    const data = await res.json();
    if (Array.isArray(data) && data.length > 0) {
        const { lat, lon } = data[0];
        return [parseFloat(lat), parseFloat(lon)];
    }
    return null;
}

const getCountryLabel = (o) => (typeof o === "string" ? o : o?.label ?? o?.name ?? o?.value ?? o?.title ?? o?.country ?? "");

const normalizeCountry = (s) =>
    canonicalCountry(String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, ""))
        .toLowerCase()
        .trim();

function SlideLeft(props) {return <Slide {...props} direction="left" />;}

export default function GlobalAgentDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [markers, setMarkers] = useState([]);
    const [filters, setFilters] = useState({ country: null });
    const [countrySearch, setCountrySearch] = useState(null);
    const [countryInput, setCountryInput] = useState("");
    const [globalSearch, setGlobalSearch] = useState("");
    const [showMap, setShowMap] = useState(false);
    // Snack
    const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });
    const notify = useCallback((message, severity = "info") => setSnack({ open: true, message, severity }), []);
    const handleSnackClose = (_e, reason) => {
        if (reason === "clickaway") return;
        setSnack((p) => ({ ...p, open: false }));
    };

    const tableAnchorRef = useRef(null);
    const [rfqList, setRfqList] = useState([]);
    const [updatingStatusIds, setUpdatingStatusIds] = useState(() => new Set());
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [salesRepOptions, setSalesRepOptions] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingRow, setEditingRow] = useState(null);

    const emptyForm = useMemo(
        () => ({
            serviceMode: "",
            transportationMode: "",
            origin: "",
            destination: "",
            pol: "",
            pod: "",
            originCity: "",
            destinationCity: "",
            cargoDescription: "",
            hsCode: "",
            valueOfCargo: "",
            valueOfCargoUnit: "",
            insurance: false,
            incoterm: "",
            grossWeightKg: "",
            volumeCbm: "",
            etd: "",
            eta: "",
            specialInstructions: "",
            note: "",
            status: "pending",
            offer: "",
            offerCurrency: "USD",
            salesRepIds: [],
            length: "",
            width: "",
            height: "",
            serviceLevel: "",
            quantity: "",
            id: null,
            rfqNumber: "",
            companies: [],
            equipmentType: "",
        }),
        []
    );

    const [requestForm, setRequestForm] = useState(emptyForm);

    const getAuthUser = () => {
        const token = localStorage.getItem("token");
        if (!token) return null;
        try {
            return jwtDecode(token);
        } catch (e) {
            console.error("Token decode failed", e);
            return null;
        }
    };

    useEffect(() => {
        const fetchSalesReps = async () => {
            try {
                const res = await CrudService.getUsers();
                const raw = res?.data?.data || res?.data || [];
                const usersArr = Array.isArray(raw) ? raw : [];

                const formattedUsers = usersArr
                    .map((u) => ({
                        id: u.id || u._id,
                        label: u?.attributes?.name || u?.name || "",
                    }))
                    .filter((x) => x.id && x.label);

                setSalesRepOptions(formattedUsers);
            } catch (error) {
                console.error("Sales reps alınamadı:", error);
                notify("Sales reps could not be loaded.", "error");
            }
        };
        fetchSalesReps();
    }, [notify]);
    useEffect(() => {
        (async () => {
            try {
                const [customerRes, agentRes] = await Promise.all([CrudService.getCustomers(), CrudService.getGlobalAgents()]);

                const customerRaw = customerRes?.data?.data || customerRes?.data || [];
                const customers = customerRaw.map((item) => ({
                    id: String(item.id || item._id),
                    type: "customer",
                    ...(item.attributes || item),
                }));

                const agentRaw = agentRes?.data?.data || agentRes?.data || [];
                const agents = agentRaw.map((item) => ({
                    id: String(item.id || item._id),
                    type: "agent",
                    ...(item.attributes || item),
                }));

                setCustomerOptions([...customers, ...agents]);
            } catch (err) {
                console.error("Error fetching customers or agents:", err);
                notify("Customer list could not be loaded.", "error");
            }
        })();
    }, [notify]);
    const getAgentHQCountries = useCallback((ga) => {
        const hqRaw = ga.globalAgentCountry ?? ga.hqCountry ?? ga.country ?? ga.originCountry ?? ga;
        const serviceRaw = ga.servicesCountry ?? ga.serviceCountry ?? ga.serviceCountries;

        const hqList = extractCountryNames(hqRaw).map(canonicalCountry);
        const svcList = extractCountryNames(serviceRaw).map(canonicalCountry);
        return Array.from(new Set([...hqList, ...svcList]));
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const response = await CrudService.getGlobalAgents();
                const arr = response?.data?.data || response?.data || [];
                const normalized = arr.map((item) => ({ id: item.id, ...(item.attributes || item) }));
                setData(normalized);
            } catch (error) {
                console.error("Error fetching globalAgents:", error);
                notify("Global Agents could not be loaded. Please try again later.", "error");
            }
        })();
    }, [notify]);

    const mapKey = useMemo(() => markers.map((m) => `${m.name}:${m.latLng?.join(",")}`).join("|"), [markers]);

    useEffect(() => {
        (async () => {
            try {
                const res = await CrudService.getSentRequests({ page: 1, size: 1000, sort: "created_at" });

                const arr = res?.data?.data || res?.data || [];
                const normalized = arr.map((item) => {
                    const req = item.request || {};
                    const customer = item.customer || req.customer || null;
                    const createdAt = item.created_at ?? item.createdAt ?? req.created_at ?? req.createdAt ?? null;
                    return {
                        ...req,
                        ...item,
                        id: item.id || item._id,
                        rfqNumber: item.rfqNumber || req.rfqNumber || "—",
                        customer,
                        customerId: customer?.id || customer?._id,
                        customerName: customer?.name || "",
                        created_at: createdAt,
                        origin: item.origin || req.origin,
                        destination: item.destination || req.destination,
                        serviceMode: item.serviceMode || req.serviceMode,
                        transportationMode: item.transportationMode || req.transportationMode,
                        incoterm: item.incoterm || req.incoterm,
                        status: item.status || req.status,
                        offer: item.offer || req.offer,
                        offerCurrency: item.offerCurrency || req.offerCurrency,
                        companies: item.companies || req.companies || [],
                        salesRepIds: item.salesRepIds || req.salesRepIds || [],
                    };
                });
                setRfqList(normalized);
            } catch (err) {
                console.error("Error fetching RFQs:", err);
                notify("RFQs could not be loaded.", "error");
            }
        })();
    }, [notify]);

    const resetEditingState = useCallback(() => {
        setEditingRow(null);
        setSelectedCustomer(null);
        setSelectedCustomerId(null);
        setRequestForm((p) => ({
            ...emptyForm,
            salesRepIds: p?.salesRepIds || [],
            status: "pending",
        }));
        setAgentMatches(null);
        setSelectedAgents({});
        setSelectMode({});
        setTableSearch({});
    }, [emptyForm]);

    const findCountryOption = useCallback((label) => {
        const want = normalizeCountry(label);
        return (countryOptions || []).find((c) => normalizeCountry(getCountryLabel(c)) === want) || null;
    }, []);

    const getEndpointSide = (serviceMode, side) => {
        if (!serviceMode) return null;
        const startsDoor = String(serviceMode).toLowerCase().startsWith("door");
        const endsDoor = String(serviceMode).toLowerCase().endsWith("door");
        if (side === "origin") return startsDoor ? "door" : "port";
        return endsDoor ? "door" : "port";
    };

    const getLocationKind = (transportationMode, serviceMode, side) => {
        const tm = String(transportationMode || "");
        if (tm === "Road FTL" || tm === "Road LTL") return "city";
        const endpoint = getEndpointSide(serviceMode, side);
        if (!endpoint) return null;
        if (tm === "Ocean FCL" || tm === "Ocean LCL" || tm === "Project") return endpoint === "door" ? "city" : "seaport";
        if (tm === "Air") return endpoint === "door" ? "city" : "airport";
        return null;
    };

    const originKind = useMemo(
        () => getLocationKind(requestForm.transportationMode, requestForm.serviceMode, "origin"),
        [requestForm.transportationMode, requestForm.serviceMode]
    );

    const destinationKind = useMemo(
        () => getLocationKind(requestForm.transportationMode, requestForm.serviceMode, "destination"),
        [requestForm.transportationMode, requestForm.serviceMode]
    );
    const optionsCache = useRef(new Map());
    const [originOptions, setOriginOptions] = useState([]);
    const [destOptions, setDestOptions] = useState([]);
    const [originLoading, setOriginLoading] = useState(false);
    const [destLoading, setDestLoading] = useState(false);

    const normalizeOptions = (payload) => {
        const raw = payload?.data?.data || payload?.data || payload?.items || payload || [];
        const arr = Array.isArray(raw) ? raw : [];
        return arr
            .map((x) => (typeof x === "string" ? x : x?.name ?? x?.label ?? x?.title ?? x?.value))
            .filter(Boolean);
    };

    const fetchOptions = useCallback(async (country, kind) => {
        if (!country || !kind) return [];
        const cacheKey = `${kind}:${country}`;
        const cached = optionsCache.current.get(cacheKey);
        if (cached && Array.isArray(cached) && cached.length > 0) return cached;
        try {
            let res;
            if (kind === "city") res = await CrudService.getCitiesByCountry(country);
            else if (kind === "seaport") res = await CrudService.getSeaportsByCountry(country);
            else if (kind === "airport") res = await CrudService.getAirportsByCountry(country);
            else return [];
            const list = normalizeOptions(res);
            if (list.length > 0) optionsCache.current.set(cacheKey, list);
            else optionsCache.current.delete(cacheKey);
            return list;
        } catch (e) {
            console.error("fetchOptions failed:", { kind, country, e });
            optionsCache.current.delete(cacheKey);
            return [];
        }
    }, []);

    useEffect(() => {
        const country = requestForm.origin;
        if (!country || !originKind) {
            setOriginOptions([]);
            return;
        }
        setOriginLoading(true);
        fetchOptions(country, originKind)
            .then(setOriginOptions)
            .finally(() => setOriginLoading(false));
    }, [requestForm.origin, originKind, fetchOptions]);

    useEffect(() => {
        const country = requestForm.destination;
        if (!country || !destinationKind) {
            setDestOptions([]);
            return;
        }
        setDestLoading(true);
        fetchOptions(country, destinationKind)
            .then(setDestOptions)
            .finally(() => setDestLoading(false));
    }, [requestForm.destination, destinationKind, fetchOptions]);

    useEffect(() => {
        setRequestForm((p) => {
            const next = { ...p };
            if (originKind === "city") next.pol = "";
            else next.originCity = "";
            if (destinationKind === "city") next.pod = "";
            else next.destinationCity = "";
            return next;
        });
    }, [originKind, destinationKind]);

    const openCreateDialog = useCallback(() => {
        const authUser = getAuthUser();
        const myRep = salesRepOptions.find((u) => String(u.id) === String(authUser?.id));
        const defaultSalesRepIds = myRep?.id ? [myRep.id] : [];
        setSelectedCustomer(null);
        setRequestForm({ ...emptyForm, salesRepIds: defaultSalesRepIds, status: "pending" });
        setOpenDialog(true);
    }, [emptyForm, salesRepOptions]);

    const [selectedCustomerId, setSelectedCustomerId] = useState(null);

    useEffect(() => {
        if (!selectedCustomerId) return;
        const c = customerOptions.find(x => String(x.id || x._id) === String(selectedCustomerId));
        if (c) setSelectedCustomer(c);
    }, [selectedCustomerId, customerOptions]);

    const handleEdit = useCallback((row) => {
        const salesRepIds = row.salesRepIds || row?.request?.salesRepIds || [];
        const nextForm = {
            ...emptyForm,
            ...row.request,
            ...row,
            id: String(row.id || row._id),
            rfqNumber: row.rfqNumber || row?.request?.rfqNumber || "—",
            companies: row.companies || row?.request?.companies || [],
            salesRepIds: Array.isArray(salesRepIds) ? salesRepIds : [],
        };
        setRequestForm(nextForm);
        const customerId = row.customer?.id || row.customerId || row.customer?._id;
        setSelectedCustomerId(String(customerId));
        setOpenDialog(true);
        setEditingRow(row);
    }, [emptyForm]);

    const handleDelete = useCallback(
        async (row) => {
            if (!await appConfirm("Are you sure you want to delete this record?")) return;
            try {
                await CrudService.deleteSentRequest(row.id);
                setRfqList((prev) => prev.filter((r) => String(r.id) !== String(row.id)));
                notify("✅ RFQ has been deleted.", "success");
            } catch (err) {
                console.error(err);
                notify("❌ RFQ hasn't been deleted.", "error");
            }
        },
        [notify]
    );

    const updateRfqStatus = async (rowId, nextStatus) => {
        const id = String(rowId);
        const status = String(nextStatus || "pending").toLowerCase();
        const prevList = rfqList;
        setRfqList((prev) => prev.map((r) => (String(r.id) === id ? { ...r, status } : r)));
        setUpdatingStatusIds((prev) => new Set(prev).add(id));
        try {
            await CrudService.updateSentRequest(id, { status });
        } catch (err) {
            console.error("Status update failed:", err);
            setRfqList(prevList);
        } finally {
            setUpdatingStatusIds((prev) => {
                const copy = new Set(prev);
                copy.delete(id);
                return copy;
            });
        }
    };
    const buildSentRequestPayload = ({ form, customerData, companies }) => {
        const reqSnapshot = { ...form, companies };
        delete reqSnapshot.id;
        delete reqSnapshot._id;
        delete reqSnapshot.rfqNumber;
        delete reqSnapshot.created_at;
        delete reqSnapshot.updated_at;
        delete reqSnapshot.createdAt;
        delete reqSnapshot.updatedAt;
        const payload = {
            customer: customerData,
            serviceMode: reqSnapshot.serviceMode,
            transportationMode: reqSnapshot.transportationMode,
            origin: reqSnapshot.origin,
            destination: reqSnapshot.destination,
            pol: reqSnapshot.pol,
            pod: reqSnapshot.pod,
            originCity: reqSnapshot.originCity,
            destinationCity: reqSnapshot.destinationCity,
            cargoDescription: reqSnapshot.cargoDescription,
            hsCode: reqSnapshot.hsCode,
            valueOfCargo: reqSnapshot.valueOfCargo,
            valueOfCargoUnit: reqSnapshot.valueOfCargoUnit,
            insurance: !!reqSnapshot.insurance,
            incoterm: reqSnapshot.incoterm,
            grossWeightKg: reqSnapshot.grossWeightKg,
            volumeCbm: reqSnapshot.volumeCbm,
            etd: reqSnapshot.etd,
            eta: reqSnapshot.eta,
            specialInstructions: reqSnapshot.specialInstructions,
            length: reqSnapshot.length,
            width: reqSnapshot.width,
            height: reqSnapshot.height,
            equipmentType: reqSnapshot.equipmentType,
            note: reqSnapshot.note,
            status: reqSnapshot.status,
            offer: reqSnapshot.offer,
            offerCurrency: reqSnapshot.offerCurrency,
            salesRepIds: reqSnapshot.salesRepIds || [],
            companies,
            request: reqSnapshot,
            serviceLevel: reqSnapshot.serviceLevel,
            quantity: reqSnapshot.quantity === "" ? null : Number(reqSnapshot.quantity),
        };
        return { data: payload, ...payload };
    };

    const upsertRfq = useCallback(
        async (form) => {
            if (!form.serviceMode || !form.transportationMode || !form.origin || !form.destination) {
                notify("Please fill in the Service-Transportation Mode, Origin, and Destination fields.", "warning");
                return null;
            }
            const customerId = selectedCustomer?._id || selectedCustomer?.id || null;
            const customerName = selectedCustomer?.name || "";
            const customerData = { id: customerId, name: customerName };
            const companies = form.companies || [];
            const body = buildSentRequestPayload({ form, customerData, companies });
            let savedItem;
            if (form.id) {
                const updated = await CrudService.updateSentRequest(form.id, body);
                const upd =
                    updated?.data?.data?.attributes ||
                    updated?.data?.attributes ||
                    updated?.data?.data ||
                    updated?.data ||
                    {};
                savedItem = {
                    ...form,
                    ...upd,
                    ...(upd.request || {}),
                    customer: upd.customer || customerData,
                    companies: upd.companies || companies,
                    id: String(form.id),
                    rfqNumber: upd.rfqNumber || form.rfqNumber || "—",
                };
                setRfqList((prev) => prev.map((r) => (String(r.id) === String(form.id) ? { ...r, ...savedItem } : r)));
                notify("✅ RFQ updated.", "success");
            } else {
                const created = await CrudService.createSentRequest(body);
                const newData =
                    created?.data?.data?.attributes ||
                    created?.data?.attributes ||
                    created?.data?.data ||
                    created?.data ||
                    created;
                const newId =
                    created?.data?.data?.id ||
                    newData?.id ||
                    newData?._id;

                if (!newId) {
                    notify("❌ RFQ saved but ID could not be read from response.", "error");
                    return null;
                }
                const newRfqNumber = newData?.rfqNumber || "—";
                savedItem = {
                    ...form,
                    id: newId,
                    rfqNumber: newRfqNumber,
                    customer: newData?.customer || customerData,
                    companies: newData?.companies || companies,
                    ...(newData?.request || {}),
                };
                setRfqList((prev) => [savedItem, ...prev]);
                notify(`✅ RFQ created (${newRfqNumber}).`, "success");
            }
            return savedItem;
        },
        [notify, selectedCustomer]
    );

    const handleRequestSubmit = useCallback(
        async (form) => {
            try {
                const saved = await upsertRfq(form);
                if (!saved) return;

                setRequestForm((prev) => ({
                    ...prev,
                    ...saved,
                    id: saved.id || saved._id,
                    rfqNumber: saved.rfqNumber || prev.rfqNumber,
                }));

                setEditingRow(saved);
                setOpenDialog(false);
            } catch (err) {
                console.error(err);
                notify("❌ RFQ create/update failed.", "error");
            }
        },
        [upsertRfq, notify]
    );

    const mapRfqTransportationModeToQuotingType = (tm) => {
        const t = String(tm || "");
        if (t.startsWith("Road")) return "Road";
        if (t.startsWith("Ocean")) return "Ocean";
        if (t === "Air") return "Air";
        if (t === "Project") return "ProjectCargo";
        return "";
    };

    const handleConnectQuote = useCallback(
        async (form) => {
            try {
                const saved = await upsertRfq(form);
                if (!saved) return;

                const selectedTransportType = mapRfqTransportationModeToQuotingType(saved.transportationMode);
                const prefill = { ...saved };

                navigate("/quoting-tool/create-quoting-tool/details", {
                    state: {
                        selectedTransportType,
                        prefill,
                        rfq: saved,
                    },
                });

                setOpenDialog(false);
            } catch (e) {
                console.error(e);
                notify("❌ Connect Quote failed.", "error");
            }
        },
        [upsertRfq, navigate, notify]
    );


    const columns = useMemo(
        () => [
            {
                Header: "RFQ No",
                accessor: "rfqNumber",
                Cell: ({ row, value }) => (
                    <ListLink onClick={() => handleEdit(row.original)}>{value || "—"}</ListLink>
                ),
            },
            {
                Header: "Customer",
                accessor: "customer",
                Cell: ({ value }) => {
                    return <ListText primary={<ListTruncate text={value?.name || "—"} max={32} />} />;
                },
            },
            { Header: "Origin", accessor: "origin" },
            { Header: "Destination", accessor: "destination" },
            { Header: "Service Mode", accessor: "serviceMode" },
            { Header: "Transportation Mode", accessor: "transportationMode" },
            {
                Header: "Status",
                accessor: "status",
                Cell: ({ row, value }) => {
                    const rowId = row?.original?.id;
                    const current = String(value || "pending").toLowerCase();
                    const isUpdating = updatingStatusIds.has(String(rowId));

                    return (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Select
                                size="small"
                                value={current}
                                disabled={!rowId || isUpdating}
                                onChange={(e) => updateRfqStatus(rowId, e.target.value)}
                                renderValue={(val) => {
                                    const meta = getStatusMeta(val);
                                    return (
                                        <Box display="flex" alignItems="center">
                                            <StatusDot status={val} />
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{meta.label}</span>
                                        </Box>
                                    );
                                }}
                                sx={{ minWidth: 150, "& .MuiSelect-select": { display: "flex", alignItems: "center" } }}
                            >
                                {Object.keys(STATUS_META).map((key) => (
                                    <MenuItem key={key} value={key}>
                                        <Box display="flex" alignItems="center">
                                            <StatusDot status={key} />
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{STATUS_META[key].label}</span>
                                        </Box>
                                    </MenuItem>
                                ))}
                                {!STATUS_META[current] && (
                                    <MenuItem value={current} disabled>
                                        <Box display="flex" alignItems="center">
                                            <StatusDot status={current} />
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{getStatusMeta(current).label}</span>
                                        </Box>
                                    </MenuItem>
                                )}
                            </Select>
                            {isUpdating && <CircularProgress size={16} />}
                        </Box>
                    );
                },
            },
            {
                Header: "RFQ Requested Date",
                accessor: "etd",
                Cell: ({ value }) => (value ? dayjs(value).tz("Europe/Istanbul").format("DD/MM/YYYY") : "—"),
            },
            {
                Header: "Actions",
                accessor: "actions",
                disableSorting: true,
                Cell: ({ row }) => (
                    <CrmRowActions onEdit={() => handleEdit(row.original)} onDelete={() => handleDelete(row.original)} />
                ),
            },
        ],
        [handleEdit, handleDelete, updatingStatusIds]
    );

    const sortedRfq = useMemo(() => {
        return [...rfqList].sort((a, b) => {
            const da = a?.etd ? dayjs(a.etd).valueOf() : -Infinity;
            const db = b?.etd ? dayjs(b.etd).valueOf() : -Infinity;

            return da - db;
        });
    }, [rfqList]);

    const filteredRfqRows = useMemo(() => {
        const q = (globalSearch || "").toLowerCase().trim();
        if (!q) return sortedRfq;

        return sortedRfq.filter((row) => {
            return (
                (row.rfqNumber && row.rfqNumber.toLowerCase().includes(q)) ||
                (row.origin && row.origin.toLowerCase().includes(q)) ||
                (row.destination && row.destination.toLowerCase().includes(q)) ||
                (row.serviceMode && row.serviceMode.toLowerCase().includes(q)) ||
                (row.transportationMode && row.transportationMode.toLowerCase().includes(q)) ||
                (row.status && row.status.toLowerCase().includes(q))
            );
        });
    }, [sortedRfq, globalSearch]);
    const findCountryCode = (label) => {
        const want = normalizeCountry(label);
        const item = (countryOptions || []).find((c) => normalizeCountry(getCountryLabel(c)) === want);
        return item?.code || item?.alpha2 || item?.cca2 || item?.iso2 || null;
    };
    const flagUrl = (code) => (code ? `https://flagcdn.com/w20/${code.toLowerCase()}.png` : null);
    const emailRegex = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
    const emailsFrom = (v) => {
        if (!v) return [];
        if (Array.isArray(v)) return v.flatMap((x) => emailsFrom(x));
        if (typeof v === "object") {
            const keys = ["email", "mail", "contactEmail", "contact", "contactInfo", "emails"];
            return keys.flatMap((k) => emailsFrom(v[k]));
        }
        if (typeof v === "string") return v.match(emailRegex) || [];
        return [];
    };
    const splitEmails = (s) =>
        String(s || "")
            .split(/[;,]/)
            .map((x) => x.trim())
            .filter(Boolean);

    const getAgentName = (ga) =>
        ga.companyName ?? ga.name ?? ga.title ?? ga.agentName ?? ga.firmName ?? `Agent #${ga.id}`;

    const getAgentContacts = (ga) => {
        const list = new Set();
        if (Array.isArray(ga.representatives)) {
            ga.representatives.forEach((rep) => {
                emailsFrom(rep?.contactInfo).forEach((e) => list.add(e));
                emailsFrom(rep?.email).forEach((e) => list.add(e));
            });
        }
        ["email", "mail", "contactEmail", "contact", "contactInfo", "emails"].forEach((k) =>
            emailsFrom(ga?.[k]).forEach((e) => list.add(e))
        );

        return Array.from(list).join(", ");
    };

    const plainTextVersion = (html) => html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();

    const buildRecipients = (rows, kind, recipientCountry) => {
        return rows.map((row) => ({
            agentId: row.id,
            agentName: getAgentName(row),
            emails: splitEmails(getAgentContacts(row) || ""),
            kind, // "origin" | "destination"
            recipientCountry,
        }));
    };

    const [agentMatches, setAgentMatches] = useState(null);
    const [selectMode, setSelectMode] = useState({});
    const [selectedAgents, setSelectedAgents] = useState({});
    const [tableSearch, setTableSearch] = useState({});
    const [pendingDrafts, setPendingDrafts] = useState([]);
    const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
    const [emailPreviewData, setEmailPreviewData] = useState({ to: "", cc: "", subject: "", body: "" });

    const isSelecting = (country) => !!selectMode[country];
    const toggleSelectMode = (country) => setSelectMode((prev) => ({ ...prev, [country]: !prev[country] }));
    const toggleAgentSelection = (country, id) =>
        setSelectedAgents((prev) => {
            const list = prev[country] || [];
            const exists = list.includes(id);
            const next = exists ? list.filter((x) => x !== id) : [...list, id];
            return { ...prev, [country]: next };
        });
    const clearSelection = (country) => setSelectedAgents((prev) => ({ ...prev, [country]: [] }));
    const selectAll = (country, items) => setSelectedAgents((prev) => ({ ...prev, [country]: items.map((r) => r.id) }));

    const buildEmailBody = (companyName) => {
        const L = (k, v) =>
            `<tr>
      <td style="padding:4px 8px;border:1px solid #ccc;width:40%;vertical-align:top;"><b>${k}</b></td>
      <td style="padding:4px 8px;border:1px solid #ccc;width:60%;vertical-align:top;">
        ${v && String(v).trim() !== "" ? v : "—"}
      </td>
    </tr>`;

        return `
  <div style="font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#333;line-height:1.6;max-width:650px;margin:0;text-align:left;">
    <p>Dear <b>${companyName}</b> Team, </p>

    <p>
      This RFQ is shared by <b>Filenders Logistics & Technology Inc. </b> exclusively with our approved partners.<br/>
      We kindly request your support for the shipment below. Please review the details and provide your best offer at your earliest convenience.<br/>
      <p> <b>We look forward to your response for the request below.</b> <p>
    </p>

    <h3 style="margin-top:16px;color:#2c3e50;">Shipment Details</h3>
    <table style="border-collapse:collapse;width:100%;margin:10px 0;table-layout:fixed;">
      ${L("Service Mode", requestForm.serviceMode)}
      ${L("Transportation Mode", requestForm.transportationMode)}
      ${L("Origin (Port/Airport/Address)", requestForm.origin)}
      ${L("Destination (Port/Airport/Address)", requestForm.destination)}
      ${L("POL / Origin Location", requestForm.pol || requestForm.originCity)}
      ${L("POD / Destination Location", requestForm.pod || requestForm.destinationCity)}
      ${L("Cargo Description", requestForm.cargoDescription)}
      ${L("HS Code (if available)", requestForm.hsCode)}
      ${L("Value of Cargo", requestForm.valueOfCargo)}
      ${L("Unit", requestForm.valueOfCargoUnit)}
      ${L("Insurance (if applicable)", requestForm.insurance ? "Yes" : "No")}
      ${L("Incoterm", requestForm.incoterm)}
      ${L("Gross Weight (kg)", requestForm.grossWeightKg)}
      ${L("Volume (cbm)", requestForm.volumeCbm)}
      ${L("RFQ Requested Date", requestForm.etd)}
      ${L("RFQ Deadline", requestForm.eta)}
      ${L("Special Instructions", requestForm.specialInstructions)}
    </table>

    <p><b>Note:</b> ${requestForm.note || "SOW will be door to door service."}</p>

<table style="margin-top:28px;border-collapse:collapse;font-family:Segoe UI,Arial,sans-serif;text-align:left;">
  <tr>
    <td style="vertical-align:top;padding-right:16px;border-right:2px solid #e5e7eb;">
      <img
        src="https://filentech.com/filenders_logo.png"
        alt="Filenders"
        style="width:145px;height:auto;display:block;"
      />
    </td>

    <td style="vertical-align:top;padding-left:16px;color:#333;font-size:13px;line-height:1.5;">
      <p style="margin:0;font-size:15px;font-weight:700;color:#1f2937;">
        Filenders RFQ Team
      </p>

      <p style="margin:2px 0 8px 0;color:#6b7280;">
       Filenders Logistics and Technology
      </p>

      <p style="margin:0;">
        <b>Email:</b>
        <a href="mailto:rfq@filenders.com" style="color:#1a73e8;text-decoration:none;">
          rfq@filenders.com
        </a>
      </p>

      <p style="margin:0;">
        <b>Web:</b>
        <a href="https://www.filenders.com" style="color:#1a73e8;text-decoration:none;">
          www.filenders.com
        </a>
      </p>
       <p style="margin:0;">
        <b>Phone:</b>
        <a href="+90 324 503 84 33" style="color:#1a73e8;text-decoration:none;">
          +90 324 503 84 33
        </a>
      </p>
    </td>
  </tr>
</table>
<p style="margin-top:18px;margin-bottom:0;">
<p> Thank you and best regards,</p>
<p> Sincerely,</p>

<p> <b>Filenders Logistics & Technology Inc. </b> </p>
</p>
  </div>`;
    };

    const prepareEmailData = (row, subject) => {
        const toList = splitEmails(getAgentContacts(row));
        const cc = "emre.ayranci@filenders.com, rfq@filenders.com";
        return {
            to: toList.join(",") || "",
            cc,
            subject,
            body: buildEmailBody(getAgentName(row)),
        };
    };

    const confirmAndSendEmail = async () => {
        const { to, cc, subject, body } = emailPreviewData;
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/email/Send-meeting-recap`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({
                    to,
                    cc,
                    subject,
                    body,
                    text: plainTextVersion(body),
                }),
            });

            const result = await res.json();
            if (res.ok) {
                notify(`✅ Email sent to ${to}`, "success");
                const [, ...rest] = pendingDrafts;
                setPendingDrafts(rest);

                if (rest.length > 0) {
                    setEmailPreviewData(rest[0]);
                    setEmailPreviewOpen(true);
                    notify(`📧 Next draft (${pendingDrafts.length - rest.length + 1}/${pendingDrafts.length})`, "info");
                } else {
                    setEmailPreviewOpen(false);
                    notify("✅ All emails sent.", "success");
                }
            } else {
                notify("❌ Failed to send email: " + (result.error || "Unknown error"), "error");
            }
        } catch (err) {
            console.error(err);
            notify("❌ Error sending email.", "error");
        }
    };

    const sendEmailsForCountry = async (country) => {
        if (!agentMatches) return;

        const ids = selectedAgents[country] || [];
        if (!ids.length) {
            notify("Please select at least one agent.", "warning");
            return;
        }

        const allRows = agentMatches.byCountry[country] || [];
        const selectedRows = allRows.filter((r) => ids.includes(r.id));
        const customerName =
            selectedCustomer?.name ||
            requestForm?.customer?.name ||
            "Customer";

        const subject =
            `${requestForm.rfqNumber || "RFQ"} | ${customerName} | ` +
            `${getCountryLabel(requestForm.origin)} → ${getCountryLabel(requestForm.destination)}` +
            `${requestForm.transportationMode ? ` (${requestForm.transportationMode})` : ""}`;

        const kind =
            country === agentMatches?.origin ? "origin" : country === agentMatches?.destination ? "destination" : "unknown";

        try {
            if (!requestForm.id) {
                notify("⚠️ Please create/save the RFQ first before sending emails.", "warning");
                return;
            }

            // ✅ recipients ekle
            const recipients = buildRecipients(selectedRows, kind, country);
            if (recipients.length) {
                await CrudService.addRecipientsToSentRequest(requestForm.id, recipients);
                notify(`${recipients.length} recipient(s) added.`, "info");
            }

            // ✅ companies merge (string isimler)
            const companyNames = selectedRows.map((r) => getAgentName(r));
            const currentCompanies = requestForm.companies || [];
            const merged = Array.from(new Set([...(currentCompanies || []), ...companyNames]));

            await CrudService.updateSentRequest(requestForm.id, { companies: merged });
            setRequestForm((p) => ({ ...p, companies: merged }));
            setRfqList((prev) => prev.map((r) => (String(r.id) === String(requestForm.id) ? { ...r, companies: merged } : r)));

            // ✅ draft hazırla
            const drafts = selectedRows.map((row) => prepareEmailData(row, subject));
            setPendingDrafts(drafts);

            if (drafts.length > 0) {
                setEmailPreviewData(drafts[0]);
                setEmailPreviewOpen(true);
            }

            notify("📧 Draft(s) ready for preview.", "success");
        } catch (err) {
            console.error(err);
            notify("❌ Failed to send emails.", "error");
        }
    };

    useEffect(() => {
        if (!requestForm.origin || !requestForm.destination) return;

        const originNorm = normalizeCountry(requestForm.origin);
        const destNorm = normalizeCountry(requestForm.destination);
        const byCountry = {};
        const originAgents = data
            .map((a) => ({ ...a, name: getAgentName(a) })) // tabloda row.name için
            .filter((a) => getAgentHQCountries(a).some((c) => normalizeCountry(c) === originNorm));
        const destAgents = data
            .map((a) => ({ ...a, name: getAgentName(a) }))
            .filter((a) => getAgentHQCountries(a).some((c) => normalizeCountry(c) === destNorm));

        if (originAgents.length) byCountry[requestForm.origin] = originAgents;
        if (destAgents.length) byCountry[requestForm.destination] = destAgents;

        setAgentMatches({
            origin: requestForm.origin,
            destination: requestForm.destination,
            byCountry,
        });
    }, [requestForm.origin, requestForm.destination, data, getAgentHQCountries]);

    useEffect(() => {
        if (!emailPreviewOpen) return;
        setEmailPreviewData((prev) => {
            const nextSubject = prev.subject || `RFQ – ${requestForm.origin} → ${requestForm.destination}`;
            const nextBody = prev.body || buildEmailBody("Agent");
            if (prev.subject === nextSubject && prev.body === nextBody) return prev;
            return { ...prev, subject: nextSubject, body: nextBody };
        });
    }, [emailPreviewOpen, requestForm.origin, requestForm.destination]);


    const countryStats = useMemo(() => {
        const stats = {};

        data.forEach((agent) => {
            const countries = getAgentHQCountries(agent);

            countries.forEach((country) => {
                const label = canonicalCountry(country);

                if (!stats[label]) {
                    stats[label] = {
                        country: label,
                        agentCount: 0,
                        emailCount: 0,
                    };
                }

                stats[label].agentCount += 1;

                const emails = splitEmails(getAgentContacts(agent));
                stats[label].emailCount += emails.length;
            });
        });

        return stats;
    }, [data, getAgentHQCountries]);

    useEffect(() => {
        if (!data.length) return;
        const controller = new AbortController();

        (async () => {
            const unique = new Set();

            for (const ga of data) {
                const names = getAgentHQCountries(ga);
                for (const n of names) unique.add(n);
            }

            const countries = Array.from(unique);

            const tasks = countries.map(async (n) => {
                const label = canonicalCountry(n);

                if (COUNTRY_COORDS[label]) {
                    return { name: label, latLng: COUNTRY_COORDS[label] };
                }

                if (coordsCache.has(label)) {
                    return { name: label, latLng: coordsCache.get(label) };
                }

                try {
                    const fetched = await fetchCountryCoordinates(label, controller.signal);

                    if (fetched) {
                        coordsCache.set(label, fetched);

                        return {
                            name: label,
                            latLng: fetched,
                        };
                    }
                } catch {}

                return null;
            });

            const results = await Promise.allSettled(tasks);

            const built = [];

            for (const r of results) {
                if (r.status === "fulfilled" && r.value?.latLng) {
                    const stat = countryStats[r.value.name] || {};

                    built.push({
                        ...r.value,

                        agentCount: stat.agentCount || 0,
                        emailCount: stat.emailCount || 0,

                        style: {
                            r: Math.min(18, 7 + (stat.agentCount || 0)),

                            fill:
                                (stat.agentCount || 0) >= 10
                                    ? "#16a34a"
                                    : (stat.agentCount || 0) >= 5
                                        ? "#f59e0b"
                                        : "#1976d2",
                        },
                    });
                }
            }

            built.sort((a, b) => a.name.localeCompare(b.name));

            setMarkers(built);
        })();

        return () => controller.abort();
    }, [data, getAgentHQCountries, countryStats]);

    const visibleMarkers = useMemo(() => {
        if (!filters.country) return markers;

        return markers.filter((m) => m.name === filters.country);
    }, [markers, filters.country]);


    const markerSeriesValues = useMemo(() => {
        return visibleMarkers.reduce((acc, m, i) => {
            acc[i] =
                m.agentCount >= 10
                    ? "high"
                    : m.agentCount >= 5
                        ? "medium"
                        : "low";

            return acc;
        }, {});
    }, [visibleMarkers]);

    const escapeHtml = (value = "") =>
        String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    return (
        <MDBox
            sx={{
                minHeight: "100vh",
                background:
                    "#f8fafc",
            }}
        >
            <DashboardLayout>
                <DashboardNavbar />
                <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    open={snack.open}
                    onClose={handleSnackClose}
                    autoHideDuration={4500}
                    TransitionComponent={SlideLeft}
                >
                    <Alert onClose={handleSnackClose} severity={snack.severity} variant="filled" sx={{ width: "100%" }}>
                        {snack.message}
                    </Alert>
                </Snackbar>

                <MDBox pt={3} pb={3}>

                    <div ref={tableAnchorRef} />
                    {agentMatches && (agentMatches.origin || agentMatches.destination) ? (
                        <MDBox mt={3}>
                            <Grid container spacing={2}>
                                {["origin", "destination"].map((kind) => {
                                    const country = agentMatches[kind];
                                    if (!country) return null;

                                    const code = findCountryCode(country);
                                    const rows = agentMatches.byCountry[country] || [];
                                    const selecting = isSelecting(country);
                                    const selectedList = selectedAgents[country] || [];
                                    const selectedCount = selectedList.length;

                                    const q = (tableSearch[country] || "").toLowerCase();
                                    const visibleRows = q
                                        ? rows.filter(
                                            (r) =>
                                                (r.name || "").toLowerCase().includes(q) ||
                                                (getAgentContacts(r) || "").toLowerCase().includes(q)
                                        )
                                        : rows;

                                    return (
                                        <Grid item xs={12} md={6} key={kind}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    borderRadius: "10px",
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                    overflow: "hidden",
                                                    boxShadow: "none",
                                                    transition: "all .25s ease",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "none",
                                                    },
                                                }}
                                            >
                                                <MDBox
                                                    px={2}
                                                    py={1.25}
                                                    sx={{ background:
                                                            "#f8fafc", borderBottom: "1px solid", borderColor: "grey.200" }}
                                                >
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                                        <Stack direction="row" alignItems="center" spacing={1.25}>
                                                            {code && (
                                                                <Avatar
                                                                    variant="rounded"
                                                                    src={flagUrl(code)}
                                                                    alt={country}
                                                                    sx={{ width: 24, height: 16, boxShadow: "none", mr: 1 }}
                                                                    imgProps={{ loading: "lazy" }}
                                                                />
                                                            )}
                                                            <MDTypography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                                                {kind === "origin" ? "Origin" : "Destination"} — {country}
                                                            </MDTypography>
                                                            <Chip size="small" label={`${rows.length} GA`} />
                                                        </Stack>

                                                        <Stack direction="row" alignItems="center" spacing={1.25}>
                                                            <OutlinedInput
                                                                value={tableSearch[country] || ""}
                                                                onChange={(e) => setTableSearch((p) => ({ ...p, [country]: e.target.value }))}
                                                                placeholder="Search company or mail"
                                                                size="small"
                                                                sx={{ height: 34, bgcolor: "#fff",     borderRadius: "10px", width: 240 }}
                                                                startAdornment={
                                                                    <InputAdornment position="start">
                                                                        <SearchIcon fontSize="small" />
                                                                    </InputAdornment>
                                                                }
                                                            />

                                                            {!selecting ? (
                                                                <>
                                                                    {selectedCount > 0 && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            color="success"
                                                                            sx={{ backgroundColor: "success.main", color: "#fff", "&:hover": { color: "#fff" } }}
                                                                            onClick={() => sendEmailsForCountry(country)}
                                                                            disabled={selectedCount === 0}
                                                                        >
                                                                            Sent Mail
                                                                        </Button>
                                                                    )}

                                                                    <Tooltip title={rows.length === 0 ? "No companies" : "Select companies"}>
                                                                          <span>
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            color="success"
                                                                            onClick={() => toggleSelectMode(country)}
                                                                            disabled={rows.length === 0}
                                                                            sx={{
                                                                                backgroundColor: "success.main",
                                                                                color: "#fff",
                                                                                "&:hover": { backgroundColor: "success.dark", color: "#fff" },
                                                                                "&.Mui-disabled": { backgroundColor: "success.light", color: "#fff", opacity: 0.6 },
                                                                            }}
                                                                        >
                                                                              Select
                                                                            </Button>
                                                                          </span>
                                                                    </Tooltip>
                                                                </>
                                                            ) : (
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <MDTypography variant="caption" sx={{ mr: 0.5 }}>
                                                                        {selectedCount} selected
                                                                    </MDTypography>

                                                                    <Tooltip title="Select all">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => selectAll(country, visibleRows)}
                                                                            disabled={visibleRows.length === 0}
                                                                        >
                                                                            <DoneAllRoundedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>

                                                                    <Tooltip title="Clear">
                                                                        <IconButton size="small" onClick={() => clearSelection(country)}>
                                                                            <ClearRoundedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>

                                                                    <Tooltip title="Done">
                                                                        <IconButton size="small" color="primary" onClick={() => toggleSelectMode(country)}>
                                                                            <DoneRoundedIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>

                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        sx={{ backgroundColor: "success.main", color: "#fff", "&:hover": { color: "#fff" } }}
                                                                        onClick={() => sendEmailsForCountry(country)}
                                                                        disabled={selectedCount === 0}
                                                                    >
                                                                        Sent Mail
                                                                    </Button>
                                                                </Stack>
                                                            )}
                                                        </Stack>
                                                    </Stack>
                                                </MDBox>

                                                <TableContainer sx={{ maxHeight: 360 }}>
                                                    <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
                                                        <colgroup>
                                                            <col style={{ width: 44 }} />
                                                            <col style={{ width: 56 }} />
                                                            <col style={{ width: "45%" }} />
                                                            <col style={{ width: "55%" }} />
                                                        </colgroup>

                                                        <TableHead
                                                            sx={{
                                                                "& th": {
                                                                    fontWeight: 700,
                                                                    fontSize: "12px",
                                                                    letterSpacing: ".4px",
                                                                    textTransform: "uppercase",
                                                                    color: "#64748b",
                                                                    background: "#f8fafc",
                                                                    borderBottom: "1px solid #e2e8f0",
                                                                },
                                                            }}
                                                        >
                                                            <TableRow
                                                                sx={{
                                                                    height: 56,
                                                                }}
                                                            >
                                                                <TableCell padding="checkbox" />
                                                                <TableCell>#</TableCell>
                                                                <TableCell>Company</TableCell>
                                                            </TableRow>
                                                        </TableHead>

                                                        <TableBody>
                                                            {visibleRows.length ? (
                                                                visibleRows.map((row, i) => {
                                                                    const checked = selectedList.includes(row.id);
                                                                    return (
                                                                        <TableRow
                                                                            hover
                                                                            key={`${country}-${row.id}-${i}`}
                                                                            onClick={() => isSelecting(country) && toggleAgentSelection(country, row.id)}
                                                                            sx={{ cursor: isSelecting(country) ? "pointer" : "default" }}
                                                                        >
                                                                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                                                {isSelecting(country) && (
                                                                                    <Checkbox
                                                                                        size="small"
                                                                                        checked={checked}
                                                                                        onChange={() => toggleAgentSelection(country, row.id)}
                                                                                    />
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>{i + 1}</TableCell>
                                                                            <TableCell>{row.name}</TableCell>
                                                                            <TableCell
                                                                                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                                                                title={getAgentContacts(row) || ""}
                                                                            >
                                                                                {getAgentContacts(row) || "—"}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })
                                                            ) : (
                                                                <TableRow>
                                                                    <TableCell colSpan={4}>
                                                                        <MDTypography variant="body2" color="text">
                                                                            No agents found.
                                                                        </MDTypography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </MDBox>
                    ) : null}
                </MDBox>

                {/* ✅ Dialog */}
                <RfqFormDialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    form={requestForm}
                    setForm={setRequestForm}
                    onSubmit={handleRequestSubmit}
                    onConnectQuote={handleConnectQuote}
                    onNewCustomer={() => navigate("/customer/create-customer/details")}
                    customerOptions={customerOptions}
                    selectedCustomer={selectedCustomer}
                    setSelectedCustomer={setSelectedCustomer}
                    salesRepOptions={salesRepOptions}
                    countryOptions={countryOptions}
                    getCountryLabel={getCountryLabel}
                    canonicalCountry={canonicalCountry}
                    findCountryOption={findCountryOption}
                    originKind={originKind}
                    destinationKind={destinationKind}
                    originOptions={originOptions}
                    destOptions={destOptions}
                    originLoading={originLoading}
                    destLoading={destLoading}
                    STATUS_META={STATUS_META}
                    StatusDot={StatusDot}
                />

                <Dialog open={emailPreviewOpen} onClose={() => setEmailPreviewOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box>
                                <MDTypography variant="h5" fontWeight="bold">
                                    Email Draft Preview
                                </MDTypography>

                                {pendingDrafts.length > 0 && (
                                    <MDTypography variant="caption" color="text">
                                        Draft 1 of {pendingDrafts.length}
                                    </MDTypography>
                                )}
                            </Box>

                            <IconButton onClick={() => setEmailPreviewOpen(false)}>
                                <ClearRoundedIcon />
                            </IconButton>
                        </Stack>
                    </DialogTitle>

                    <DialogContent dividers>
                        <div style={{ marginBottom: 16 }}>
                            <strong>To:</strong>
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => setEmailPreviewData((prev) => ({ ...prev, to: e.target.innerText.trim() }))}
                                style={{ border: "1px solid #ccc", padding: "8px", minHeight: "30px", borderRadius: "4px", marginTop: "4px" }}
                            >
                                {emailPreviewData.to}
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <strong>CC:</strong>
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => setEmailPreviewData((prev) => ({ ...prev, cc: e.target.innerText.trim() }))}
                                style={{ border: "1px solid #ccc", padding: "8px", minHeight: "30px", borderRadius: "4px", marginTop: "4px" }}
                            >
                                {emailPreviewData.cc}
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <strong>Subject:</strong>
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => setEmailPreviewData((prev) => ({ ...prev, subject: e.target.innerText.trim() }))}
                                style={{ border: "1px solid #ccc", padding: "8px", minHeight: "30px", borderRadius: "4px", marginTop: "4px" }}
                            >
                                {emailPreviewData.subject}
                            </div>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <strong>Body:</strong>
                            <div
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => setEmailPreviewData((prev) => ({ ...prev, body: e.target.innerHTML }))}
                                dangerouslySetInnerHTML={{ __html: emailPreviewData.body }}
                                style={{
                                    border: "1px solid #ccc",
                                    padding: "16px",
                                    minHeight: "300px",
                                    borderRadius: "4px",
                                    backgroundColor: "#f9f9f9",
                                    fontFamily: "'Segoe UI', sans-serif",
                                    fontSize: "15px",
                                    lineHeight: "1.6",
                                    color: "#333",
                                    marginTop: "4px",
                                }}
                            />
                        </div>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button
                            onClick={() => setEmailPreviewOpen(false)}
                            sx={{ textTransform: "none", borderRadius: 2 }}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            onClick={confirmAndSendEmail}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                px: 3,
                                color: "#fff",
                                bgcolor: "success.main",
                                "&:hover": {
                                    bgcolor: "success.dark",
                                    color: "#fff",
                                },
                            }}
                        >
                            Approve and Send
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* RFQ List */}
                <MDBox>
                        <CrmPageHeader
                            title="Requests for Quotation"
                            subtitle="Quotation requests sent to agents, with matched partners and email drafts. Open an RFQ number to edit it."
                            actions={
                                <>
                                    <TextField
                                        size="small"
                                        placeholder="Search RFQ..."
                                        value={globalSearch}
                                        onChange={(e) => setGlobalSearch(e.target.value)}
                                        sx={{ minWidth: { xs: "100%", sm: 240 }, "& .MuiOutlinedInput-root": { borderRadius: "8px", bgcolor: "#fff", height: 40 } }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <CrmActionButton
                                        variant="secondary"
                                        icon={<PublicIcon />}
                                        onClick={() => setShowMap((prev) => !prev)}
                                    >
                                        {showMap ? "Hide Map" : "Show Map"}
                                    </CrmActionButton>
                                    <CrmActionButton icon={<Add />} onClick={openCreateDialog}>
                                        New Request
                                    </CrmActionButton>
                                </>
                            }
                        />

                    {editingRow && (
                        <MDBox
                            mb={2}
                            p={2}
                            borderRadius={2}
                            sx={{ backgroundColor: "#e3f2fd", border: "1px solid #90caf9", position: "relative" }}
                        >
                            <MDTypography variant="subtitle2" color="primary" fontWeight="medium">
                                Editing Selected RFQ
                            </MDTypography>

                            <MDTypography variant="body2">
                                <b>{requestForm.rfqNumber || "—"}</b> |{" "}
                                <b>{requestForm.origin || "—"}</b> → <b>{requestForm.destination || "—"}</b> |{" "}
                                {requestForm.serviceMode || "—"} - {requestForm.transportationMode || "—"} | Status:{" "}
                                <b>{requestForm.status || "—"}</b>
                            </MDTypography>

                            <IconButton size="small" sx={{ position: "absolute", top: 8, right: 8 }} onClick={resetEditingState}>
                                <ClearRoundedIcon fontSize="small" />
                            </IconButton>
                        </MDBox>
                    )}

                    {showMap && (
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: "10px",
                                border: "1px solid",
                                borderColor: "grey.200",
                                boxShadow: "none",
                                overflow: "hidden",
                                transition: "all .25s ease",
                                mb: 3,
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            <MDBox px={2.5} py={2} sx={{ borderBottom: "1px solid", borderColor: "grey.200" }}>
                                <MDTypography variant="h6" fontWeight="bold">
                                    Global Partner Map
                                </MDTypography>
                            </MDBox>

                            <Grid container>
                                <Grid item xs={12} md={9} sx={{ height: { xs: "55vh", md: "72vh" }, position: "relative" }}>
                                    <VectorMap
                                        key={mapKey}
                                        map={worldMerc}
                                        zoomOnScroll={false}
                                        zoomButtons={false}
                                        markers={visibleMarkers}
                                        backgroundColor="transparent"
                                        style={{ width: "100%", height: "100%" }}
                                        regionStyle={{ initial: { fill: "#e5e7eb" } }}
                                        series={{
                                            markers: [
                                                {
                                                    attribute: "fill",
                                                    scale: {
                                                        low: "#1976d2",
                                                        medium: "#f59e0b",
                                                        high: "#16a34a",
                                                    },
                                                    values: markerSeriesValues,
                                                },
                                                {
                                                    attribute: "r",
                                                    scale: [7, 18],
                                                    values: visibleMarkers.reduce((acc, m, i) => {
                                                        acc[i] = m.agentCount || 1;
                                                        return acc;
                                                    }, {}),
                                                },
                                            ],
                                        }}
                                        onMarkerTipShow={(e, label, index) => {
                                            const marker = visibleMarkers[index];

                                            label.html(`
        <div style="
            min-width:220px;
            padding:14px;
            border-radius:16px;
            background:white;
            box-shadow:0 20px 50px rgba(0,0,0,.18);
            font-family:Inter,sans-serif;
        ">

            <div style="
                font-size:16px;
                font-weight:800;
                color:#0f172a;
                margin-bottom:10px;
            ">
               ${escapeHtml(marker.name)}
            </div>

            <div style="
                display:flex;
                justify-content:space-between;
                margin-bottom:8px;
            ">
                <span style="color:#64748b;font-size:13px;">
                    Global Agents
                </span>

                <span style="
                    font-weight:800;
                    color:#1976d2;
                    font-size:14px;
                ">
                    ${marker.agentCount || 0}
                </span>
            </div>

            <div style="
                display:flex;
                justify-content:space-between;
            ">
                <span style="color:#64748b;font-size:13px;">
                    Email Request
                </span>

                <span style="
                    font-weight:800;
                    color:#16a34a;
                    font-size:14px;
                ">
                    ${marker.emailCount || 0}
                </span>
            </div>
        </div>
    `);
                                        }}
                                        markerStyle={{
                                            initial: {
                                                fill: "#1976d2",
                                                stroke: "#fff",
                                                "stroke-width": 4,
                                                "stroke-opacity": 0.8,
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <MDBox
                                        p={2.5}
                                        height={{ xs: "auto", md: "72vh" }}
                                        display="flex"
                                        flexDirection="column"
                                        gap={2}
                                        sx={{
                                            background:
                                                "#f8fafc",
                                            borderLeft: { md: "1px solid" },
                                            borderColor: "grey.200",
                                        }}
                                    >
                                        <MDTypography variant="h6" fontWeight="bold">
                                            HQ Country Filter
                                        </MDTypography>

                                        <Autocomplete
                                            value={countrySearch}
                                            inputValue={countryInput}
                                            onInputChange={(_e, v) => setCountryInput(v)}
                                            onChange={(_e, val) => {
                                                setCountrySearch(val);

                                                setFilters({
                                                    country: val || null,
                                                });
                                            }}
                                            options={Array.from(new Set(markers.map((m) => m.name))).sort((a, b) =>
                                                a.localeCompare(b)
                                            )}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Search HQ Country" size="small" />
                                            )}
                                            clearOnBlur={false}
                                            autoHighlight
                                            fullWidth
                                        />

                                        {filters.country && (
                                            <Chip
                                                label={`Selected: ${filters.country}`}
                                                onDelete={() => {
                                                    setFilters({ country: null });
                                                    setCountrySearch(null);
                                                    setCountryInput("");
                                                }}
                                                sx={{ justifyContent: "space-between" }}
                                            />
                                        )}

                                        <MDBox mt="auto">
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    p: 2,
                                                    borderRadius: "10px",
                                                    border: "1px solid",
                                                    borderColor: "grey.200",
                                                    transition: "all .25s ease",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "none",
                                                    },
                                                }}
                                            >
                                                <MDTypography variant="caption" color="text">
                                                    Total Agents
                                                </MDTypography>
                                                <MDTypography variant="h5" fontWeight="bold">
                                                    {data.length}
                                                </MDTypography>
                                            </Card>
                                        </MDBox>
                                    </MDBox>
                                </Grid>
                            </Grid>
                        </Card>
                    )}

                    <CrmTableCard
                        title="RFQ list"
                        summary={`${filteredRfqRows.length} quotation requests`}
                    >
                        <DataTable
                            table={{
                                columns,
                                rows: filteredRfqRows,
                            }}
                            entriesPerPage={{ defaultValue: 100 }}
                            showTotalEntries
                            isSorted={false}
                            disableInternalSort
                        />
                    </CrmTableCard>

                    <br/><Footer />
                </MDBox>


            </DashboardLayout>
        </MDBox>
    );
}
